// ==== GROUP PLAYBACK + NEW TRACK ====
// Split from the original single tempdashboard.js (originally lines 8576-9344).
// Loaded as a plain classic <script defer> alongside the other tempdashboard-*.js files --
// they all share one global scope (like separate parts of one big file), so functions and
// top-level let/const declared in any of these files are visible to all the others, as long
// as the <script> tags stay in the same order every one of these files was split in.

  // ---- GROUP PLAYBACK: same interaction model as New Track's single-
  // tracker playback (Play/Pause/Stop/seek/speed), but drives EVERY visible
  // tracker's own arrow+trail at once from one shared time cursor, instead
  // of stepping through one tracker's point array by index. A tracker with
  // no data yet at the current cursor time just doesn't show a marker until
  // its first point's time is reached; one that's already finished stays
  // parked at its last point. ----
  let grpPlaybackStartMs = 0;
  let grpPlaybackEndMs = 0;
  let grpPlaybackCurrentMs = 0;
  let grpPlaybackTimer = null; // requestAnimationFrame id -- non-null only while actively animating
  let grpPlaybackActive = false; // true once a session has started (playing OR paused)
  let grpPlaybackMarkers = {}; // rawId -> { marker, trail, trailCasing, completedLatLngs }
  let grpFollowAnimating = false; // true while a zoom-out-to-follow fitBounds transition is still playing (same guarded pattern as New Track's ntFollowAnimating)

  // Position (interpolated) of one tracker's points array at real time t --
  // null if t is before that tracker's first point (hasn't started yet).
  function grpPositionAtTime(points, t) {
    if (points.length === 0 || t < points[0].t) return null;
    if (t >= points[points.length - 1].t) return { lat: points[points.length - 1].lat, lng: points[points.length - 1].lng, segIndex: points.length - 2 };
    for (let i = 0; i < points.length - 1; i++) {
      if (points[i].t <= t && t <= points[i + 1].t) {
        const span = points[i + 1].t - points[i].t;
        const frac = span > 0 ? (t - points[i].t) / span : 0;
        return {
          lat: points[i].lat + (points[i + 1].lat - points[i].lat) * frac,
          lng: points[i].lng + (points[i + 1].lng - points[i].lng) * frac,
          segIndex: i
        };
      }
    }
    return null;
  }

  function grpEnsurePlaybackSession() {
    if (grpPlaybackActive) return;
    grpPlaybackActive = true;
    Object.keys(grpTrackerLayers).forEach(rawId => {
      const layers = grpTrackerLayers[rawId];
      [layers.casing, layers.line, layers.decorator].forEach(l => l && grpMap.removeLayer(l));
      layers.markers.forEach(m => grpMap.removeLayer(m));
    });
    Object.keys(grpTrackerData).forEach(rawId => {
      const { color } = grpTrackerData[rawId];
      const trailCasing = L.polyline([], { color: '#ffffff', weight: 6, opacity: 0.9 });
      const trail = L.polyline([], { color: color.hex, weight: 3, opacity: 0.9 });
      const marker = L.marker([0, 0], {
        icon: L.divIcon({
          className: '',
          html: `<div class="nt-flight-arrow"><i class="fa-solid fa-dove nt-flight-arrow-shape" style="color:${color.hex};"></i></div>`,
          iconSize: [28, 28], iconAnchor: [14, 14]
        }),
        opacity: 0
      });
      if (grpVisibility[rawId] !== false) { trailCasing.addTo(grpMap); trail.addTo(grpMap); marker.addTo(grpMap); }
      grpPlaybackMarkers[rawId] = { marker, trail, trailCasing, completedLatLngs: [] };
    });
    document.getElementById('grp-play-seek').disabled = false;
  }

  function grpRenderPlaybackFrame() {
    let anyVisible = false;
    let anyOutOfView = false;
    const allKnownLatLngs = [];
    Object.keys(grpTrackerData).forEach(rawId => {
      const { points } = grpTrackerData[rawId];
      const pm = grpPlaybackMarkers[rawId];
      if (!pm) return;
      const pos = grpPositionAtTime(points, grpPlaybackCurrentMs);
      if (!pos) { pm.marker.setOpacity(0); return; }
      pm.marker.setOpacity(1);
      pm.marker.setLatLng([pos.lat, pos.lng]);
      anyVisible = true;
      // Rebuild completed-so-far latlngs up to (and including interpolated) current position.
      const upTo = points.filter(p => p.t <= grpPlaybackCurrentMs).map(p => [p.lat, p.lng]);
      const trailLatLngs = upTo.concat([[pos.lat, pos.lng]]);
      pm.trail.setLatLngs(trailLatLngs);
      pm.trailCasing.setLatLngs(trailLatLngs);
      // Only trackers the user hasn't hidden count toward "did something
      // leave the view" -- a hidden tracker's marker isn't drawn, so it
      // shouldn't be able to trigger a zoom-out nobody can see the point of.
      if (grpVisibility[rawId] !== false) {
        // Every known point (not just flown-so-far) -- see the fitBounds
        // call below for why.
        allKnownLatLngs.push(...points.map(p => [p.lat, p.lng]));
        if (!grpMap.getBounds().contains([pos.lat, pos.lng])) anyOutOfView = true;
      }
    });
    const timeEl = document.getElementById('grp-play-time');
    timeEl.textContent = fmtTime(grpPlaybackCurrentMs);
    timeEl.classList.add('nt-play-time--active');
    const overallProgress = grpPlaybackEndMs > grpPlaybackStartMs
      ? (grpPlaybackCurrentMs - grpPlaybackStartMs) / (grpPlaybackEndMs - grpPlaybackStartMs) : 0;
    document.getElementById('grp-play-seek').value = Math.round(Math.min(1, Math.max(0, overallProgress)) * 1000);

    // Same zoom-out-to-follow behavior as New Track's ntRenderPlaybackFrame,
    // fitting every VISIBLE tracker's WHOLE known flight (not just what's
    // flown so far) once any of them is about to leave the view. Fitting
    // only "so far" meant every time the next point was further out than
    // the last zoom level covered, it triggered ANOTHER zoom-out --
    // repeated back-to-back transitions that looked like the map blinking.
    // Fitting the whole known dataset up front means no later point in it
    // can fall outside this view again, so it only has to zoom out ONCE per
    // playback (twice only if a live/ongoing tracker gets genuinely new
    // data beyond what was known -- that's expected, not the bug). Animated
    // + guarded by grpFollowAnimating so a transition can't re-trigger
    // itself mid-flight.
    if (!grpFollowAnimating && anyOutOfView && allKnownLatLngs.length > 0) {
      grpFollowAnimating = true;
      grpMap.once('moveend', () => { grpFollowAnimating = false; });
      grpMap.fitBounds(L.latLngBounds(allKnownLatLngs), { padding: [40, 40] });
    }
  }

  function grpStartPlayback() {
    if (!grpMap || grpPlaybackEndMs <= grpPlaybackStartMs) return;
    const isFreshStart = !grpPlaybackActive;
    grpEnsurePlaybackSession();
    if (isFreshStart) {
      grpPlaybackCurrentMs = grpPlaybackStartMs;
      const allStartLatLngs = Object.values(grpTrackerData).flatMap(t => t.points).map(p => [p.lat, p.lng]);
      if (allStartLatLngs.length > 0) grpMap.fitBounds(L.latLngBounds(allStartLatLngs), { padding: [30, 30], maxZoom: 15, animate: true });
      grpRenderPlaybackFrame();
    }
    grpResumePlaybackLoop();
  }

  function grpResumePlaybackLoop() {
    if (grpPlaybackTimer || !grpPlaybackActive) return;
    document.getElementById('grp-play-btn').textContent = '⏸ Pause';
    document.getElementById('grp-stop-btn').disabled = false;
    let lastFrameTime = performance.now();
    const loop = (now) => {
      const speedSecPerAnimSec = parseInt(document.getElementById('grp-play-speed').value, 10);
      const deltaMs = (now - lastFrameTime) * speedSecPerAnimSec;
      lastFrameTime = now;
      grpPlaybackCurrentMs = Math.min(grpPlaybackEndMs, grpPlaybackCurrentMs + deltaMs);
      grpRenderPlaybackFrame();
      if (grpPlaybackCurrentMs >= grpPlaybackEndMs) { grpStopPlayback(); return; }
      grpPlaybackTimer = requestAnimationFrame(loop);
    };
    grpPlaybackTimer = requestAnimationFrame(loop);
  }

  function grpPausePlayback() {
    if (grpPlaybackTimer) { cancelAnimationFrame(grpPlaybackTimer); grpPlaybackTimer = null; }
    document.getElementById('grp-play-btn').textContent = '▶ Play';
  }

  function grpStopPlayback(skipRestore) {
    if (grpPlaybackTimer) { cancelAnimationFrame(grpPlaybackTimer); grpPlaybackTimer = null; }
    if (grpPlaybackActive) {
      Object.values(grpPlaybackMarkers).forEach(pm => {
        grpMap.removeLayer(pm.marker); grpMap.removeLayer(pm.trail); grpMap.removeLayer(pm.trailCasing);
      });
      grpPlaybackMarkers = {};
      grpPlaybackActive = false;
      if (!skipRestore) {
        Object.keys(grpTrackerLayers).forEach(rawId => {
          const layers = grpTrackerLayers[rawId];
          if (grpVisibility[rawId] === false) return;
          [layers.casing, layers.line, layers.decorator].forEach(l => l && l.addTo(grpMap));
          layers.markers.forEach(m => m.addTo(grpMap));
        });
      }
    }
    document.getElementById('grp-play-btn').textContent = '▶ Play';
    const hasData = Object.values(grpTrackerData).reduce((sum, t) => sum + t.points.length, 0) >= 2;
    document.getElementById('grp-play-btn').disabled = !hasData;
    document.getElementById('grp-stop-btn').disabled = true;
    const seek = document.getElementById('grp-play-seek');
    seek.disabled = !hasData;
    seek.value = 0;
    const timeEl = document.getElementById('grp-play-time');
    timeEl.textContent = 'Press play to replay this flight from the first ping to the last.';
    timeEl.classList.remove('nt-play-time--active');
  }

  function grpSeekPlayback(seekValue) {
    if (grpPlaybackEndMs <= grpPlaybackStartMs) return;
    grpPausePlayback();
    grpEnsurePlaybackSession();
    const frac = seekValue / 1000;
    grpPlaybackCurrentMs = grpPlaybackStartMs + frac * (grpPlaybackEndMs - grpPlaybackStartMs);
    grpRenderPlaybackFrame();
  }

  document.getElementById('grp-play-btn').onclick = () => {
    if (grpPlaybackTimer) grpPausePlayback();
    else if (grpPlaybackActive) grpResumePlaybackLoop();
    else grpStartPlayback();
  };
  document.getElementById('grp-stop-btn').onclick = () => grpStopPlayback();
  document.getElementById('grp-play-seek').oninput = (e) => grpSeekPlayback(parseInt(e.target.value, 10));

  // ---- NEW TRACK: single-tracker detail view, no sidebar nav item of its
  // own -- reached only via "View map" on a tracker card, same for every
  // account ----
  let ntMap = null;
  let ntMapInitialized = false;
  let ntCurrentRawId = null;
  let ntDetectionsUnsubscribe = null; // detaches the live listener set up in loadNewTrack() -- called before attaching a new one and when leaving the New Track tab
  // Same layers-control toggles as the group map, minus Direct line home /
  // Loft trajectory -- both need a release point and/or 2+ lofts, neither
  // of which exists in a single-tracker view.
  let ntShowDistanceRings = false;
  let ntShowLoftNames = false;
  let ntShowTrackerNames = false;
  let ntExtraLayersGroup = null; // loft marker + distance rings, redrawn as a whole on any toggle change
  // Which tab "← Trackers" should return to -- New Track can be opened from
  // the customer Trackers tab or a pigeon's own Map button, so the back
  // button needs to remember which one to return to.
  let ntEntryTab = 'trackers';
  let ntAllPoints = []; // every point loaded for the current tracker+window, oldest first
  let ntEarliestSavedTs = null; // earliest timestamp we have in Firestore for this tracker, ACROSS ALL TIME (not just the current window) -- lets Pull History check "do we already have this?" before calling Vernal
  const ntNoOlderHistory = new Set(); // "rawId:windowVal" keys Vernal has already confirmed have nothing older -- avoids repeating the same slow, futile round-trip every time a tracker is reopened this session
  const ntGeocodeCache = {}; // "lat,lng" (rounded) -> address string, avoids repeat Nominatim calls
  const ntPointMarkersByTime = new Map(); // point timestamp -> its Leaflet marker on the current map, so a Location History row click can find and open the right one

  // Three base map styles, swapped in/out by the map-type control -- mirrors
  // the "Light / Detailed / Satellite" picker seen on competitor sites.
  // Detailed = the original OSM standard tiles (unchanged default).
  // crossOrigin: true on all three -- every one of these tile hosts sends
  // Access-Control-Allow-Origin: * (confirmed directly), so this is safe and
  // lets the "Capture" button (html2canvas) read the map's pixels without
  // hitting a tainted-canvas security error. Without this, the tiles still
  // display fine (crossOrigin only affects how the browser fetches them),
  // but toDataURL() on the captured canvas would throw.
  const NT_TILE_LAYERS = {
    // CartoDB's free "light_all" tiles started serving a watermarked "API
    // KEY REQUIRED" placeholder instead of real tiles once anonymous usage
    // crossed their (undocumented) free quota -- confirmed directly: a
    // single request still succeeds, so it's a rate limit, not an outage,
    // meaning it would keep coming back intermittently rather than being a
    // one-off glitch. Switched to Esri's own free light-gray basemap
    // instead (same provider family already proven reliable here for
    // Satellite, no API key needed). Esri splits color/terrain (Base) and
    // roads/labels (Reference) into two layers instead of CartoDB's single
    // combined tile -- both loaded together as one group so it still looks
    // like one complete light map, same as before.
    light: () => L.layerGroup([
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19, maxNativeZoom: 16, attribution: 'Tiles &copy; Esri', crossOrigin: true
      }),
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19, maxNativeZoom: 16, crossOrigin: true
      })
    ]),
    detailed: () => L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors', crossOrigin: true
    }),
    satellite: () => L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19, attribution: 'Tiles &copy; Esri', crossOrigin: true
    })
  };
  // Matches each NT_TILE_LAYERS style's own required credit text -- shown by
  // NtAttributionControl below, swapped whenever ntSetMapType() changes style.
  const NT_TILE_ATTRIBUTION = {
    light: 'Tiles &copy; Esri',
    detailed: '&copy; OpenStreetMap contributors',
    satellite: 'Tiles &copy; Esri'
  };
  let ntAttributionTextEl = null; // set by NtAttributionControl's onAdd, updated by ntSetMapType
  let ntBaseLayer = null;
  let ntMapType = 'detailed';

  // Flight playback: animates a marker along the currently-rendered points,
  // oldest to newest, so you can watch the actual flight path unfold instead
  // of just seeing the static line. While playing, the static path/markers
  // (ntStaticLayers) are hidden and a growing trail is drawn behind the
  // arrow instead -- on Stop, the trail is removed and the static layers
  // are put back. Supports pause/resume (the Play button doubles as Pause)
  // and manual scrubbing via the seek bar.
  let ntStaticLayers = [];
  let ntPlaybackPoints = [];
  // "Locate Now"'s live-only marker (yellow) -- never part of ntStaticLayers
  // since it's not persisted/history data, just a right-now check. Tracked
  // separately so a second Locate Now click replaces it instead of
  // stacking; renderNewTrackMap's own full-layer wipe on every re-render
  // (switching tracker/window, etc.) clears it naturally too.
  let ntLocateNowMarker = null;
  let ntMyLocationMarker = null; // the viewer's own browser-geolocated position, never saved anywhere -- purely local/transient
  let ntPlaybackTimer = null; // requestAnimationFrame id -- non-null only while actively animating
  let ntPlaybackMarker = null; // non-null once a session has started (playing OR paused); null when fully stopped
  let ntPlaybackTrail = null;
  let ntPlaybackTrailCasing = null;
  let ntFollowAnimating = false; // true while a zoom-out-to-follow fitBounds transition is still playing
  let ntPlaybackCurrentMs = 0; // continuous time cursor -- same engine as Group Tracking's grpPlaybackCurrentMs, just for one tracker
  let ntPlaybackScanIndex = 0; // last known segment index -- lets the per-frame lookup resume from here instead of re-scanning the whole points array every frame (New Track can load up to a 7-day window, thousands of points -- see ntPositionAtTime)
  let ntPlaybackCompletedLatLngs = []; // built up incrementally as points are crossed, instead of re-filtering the whole array every frame
  // "Skip still time" -- fast-forwards through stretches where the tracker
  // barely moved (e.g. sitting in the loft for hours), instead of watching
  // the marker sit on one spot for a proportional chunk of the replay.
  // ntStillSegments[i] is true when the gap between points[i] and
  // points[i+1] moved under STILL_DISTANCE_METERS -- recomputed once
  // whenever ntPlaybackPoints is (re)loaded, not per-frame.
  const STILL_DISTANCE_METERS = 25;
  const STILL_SPEEDUP_FACTOR = 30;
  let ntStillSegments = [];
  let ntSkipStillTime = false;

  // Initial compass bearing (0=north, 90=east, clockwise) from p0 to p1 --
  // used to rotate the playback arrow so it visibly points the direction
  // of travel, not just glide along facing a fixed way. Group Tracking's
  // markers don't rotate at all -- this is a New Track-only feature, kept
  // even after switching to Group's timing engine below.
  function ntBearing(p0, p1) {
    const toRad = d => d * Math.PI / 180, toDeg = r => r * 180 / Math.PI;
    const lat1 = toRad(p0.lat), lat2 = toRad(p1.lat), dLng = toRad(p1.lng - p0.lng);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  }

  // Position (interpolated) of the loaded points array at real time t.
  // fromIndex lets a forward-moving caller resume scanning from wherever it
  // left off last frame instead of re-scanning the whole array from the
  // start every time -- with a big dataset (New Track can load up to a
  // 7-day window, thousands of points) re-scanning from 0 every single
  // animation frame (60x/sec) was slow enough to drop frames, and each
  // dropped frame meant a bigger position jump on the next one -- THAT was
  // what was actually causing the fast blinking: the marker kept jumping in
  // and out of the current view, re-triggering the zoom-out over and over
  // instead of the intended once. Group Tracking never hit this because its
  // per-tracker datasets are typically much smaller. fromIndex is only a
  // hint -- if it's invalid (e.g. seeking backward past it) this falls back
  // to a full scan from 0, same as before.
  function ntPositionAtTime(points, t, fromIndex) {
    if (points.length === 0 || t < points[0].t) return null;
    if (t >= points[points.length - 1].t) return { lat: points[points.length - 1].lat, lng: points[points.length - 1].lng, segIndex: points.length - 2 };
    const start = (typeof fromIndex === 'number' && fromIndex >= 0 && fromIndex < points.length - 1 && points[fromIndex].t <= t) ? fromIndex : 0;
    for (let i = start; i < points.length - 1; i++) {
      if (points[i].t <= t && t <= points[i + 1].t) {
        const span = points[i + 1].t - points[i].t;
        const frac = span > 0 ? (t - points[i].t) / span : 0;
        return {
          lat: points[i].lat + (points[i + 1].lat - points[i].lat) * frac,
          lng: points[i].lng + (points[i + 1].lng - points[i].lng) * frac,
          segIndex: i
        };
      }
    }
    return null;
  }

  // Draws the marker/trail/time/seek-bar for the current ntPlaybackCurrentMs
  // -- shared by the animation loop and manual seek-bar scrubbing so both
  // stay in sync.
  function ntRenderPlaybackFrame() {
    const pos = ntPositionAtTime(ntPlaybackPoints, ntPlaybackCurrentMs, ntPlaybackScanIndex);
    if (!pos) return;
    ntPlaybackMarker.setLatLng([pos.lat, pos.lng]);
    const el = ntPlaybackMarker.getElement();
    const shape = el && el.querySelector('.nt-flight-arrow-shape');
    if (shape) shape.style.transform = 'rotate(' + ntBearing(ntPlaybackPoints[pos.segIndex], ntPlaybackPoints[pos.segIndex + 1]) + 'deg)';

    // Grow the completed-trail cache incrementally (only the points newly
    // crossed since last frame) instead of re-filtering the whole dataset
    // every frame. Backward jumps (seeking earlier) rebuild it once, which
    // is fine since that's a rare user action, not a per-frame cost.
    if (pos.segIndex > ntPlaybackScanIndex) {
      for (let idx = ntPlaybackScanIndex + 1; idx <= pos.segIndex; idx++) {
        ntPlaybackCompletedLatLngs.push([ntPlaybackPoints[idx].lat, ntPlaybackPoints[idx].lng]);
      }
    } else if (pos.segIndex < ntPlaybackScanIndex) {
      ntPlaybackCompletedLatLngs = ntPlaybackPoints.slice(0, pos.segIndex + 1).map(p => [p.lat, p.lng]);
    }
    ntPlaybackScanIndex = pos.segIndex;

    const trailLatLngs = ntPlaybackCompletedLatLngs.concat([[pos.lat, pos.lng]]);
    ntPlaybackTrail.setLatLngs(trailLatLngs);
    ntPlaybackTrailCasing.setLatLngs(trailLatLngs);

    const timeEl = document.getElementById('nt-play-time');
    timeEl.textContent = fmtTime(ntPlaybackCurrentMs);
    timeEl.classList.add('nt-play-time--active');
    const startMs = ntPlaybackPoints[0].t, endMs = ntPlaybackPoints[ntPlaybackPoints.length - 1].t;
    const overallProgress = endMs > startMs ? (ntPlaybackCurrentMs - startMs) / (endMs - startMs) : 0;
    document.getElementById('nt-play-seek').value = Math.round(Math.min(1, Math.max(0, overallProgress)) * 1000);

    // Keep the flying marker on screen the way the reference site does it:
    // once it's about to leave the visible area, fit the view to the WHOLE
    // known flight (every point, not just start -> current), so this only
    // has to zoom out ONCE per playback instead of re-triggering every time
    // the next point happens to be a bit further out than the last zoom
    // level covered. Fitting the whole dataset up front means no later
    // point in it can ever fall outside this view again. (A live/ongoing
    // tracker getting genuinely new data beyond what was known can still
    // trigger a second one -- that's expected, not the bug.) Animated
    // (smooth), guarded by ntFollowAnimating so this can't re-trigger again
    // while the current transition is still playing.
    if (!ntFollowAnimating && !ntMap.getBounds().contains([pos.lat, pos.lng])) {
      ntFollowAnimating = true;
      ntMap.once('moveend', () => { ntFollowAnimating = false; });
      const wholeFlightLatLngs = ntPlaybackPoints.map(p => [p.lat, p.lng]);
      ntMap.fitBounds(L.latLngBounds(wholeFlightLatLngs), { padding: [40, 40] });
    }
  }

  // Creates the trail/arrow and hides the static layers if a session isn't
  // already active -- a no-op if Play/seek was already used (so pausing
  // and scrubbing don't keep recreating the marker).
  // Note: does NOT touch the map view itself -- ntStartPlayback recenters
  // on the starting point (only for a genuine Play press), and
  // ntSeekPlayback recenters on the scrubbed-to position instead. Doing it
  // here unconditionally used to force a jump to the START point even when
  // this was triggered by scrubbing the seek bar to somewhere else entirely,
  // making the very first scrub (before ever pressing Play) look broken.
  function ntEnsurePlaybackSession() {
    if (ntPlaybackMarker) return;
    ntStaticLayers.forEach(l => ntMap.removeLayer(l));
    const start = ntPlaybackPoints[0];
    ntPlaybackScanIndex = 0;
    ntPlaybackCompletedLatLngs = [[start.lat, start.lng]];
    ntPlaybackTrailCasing = L.polyline([[start.lat, start.lng]], { color: '#ffffff', weight: 6, opacity: 0.9 }).addTo(ntMap);
    ntPlaybackTrail = L.polyline([[start.lat, start.lng]], { color: '#3a3a3a', weight: 3, opacity: 0.9 }).addTo(ntMap);
    ntPlaybackMarker = L.marker([start.lat, start.lng], {
      icon: L.divIcon({
        className: '',
        html: '<div class="nt-flight-arrow"><i class="fa-solid fa-dove nt-flight-arrow-shape"></i></div>',
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      })
    }).addTo(ntMap);
    document.getElementById('nt-play-seek').disabled = false;
  }

  function ntStartPlayback() {
    if (!ntMap || ntPlaybackPoints.length < 2) return;
    const isFreshStart = !ntPlaybackMarker;
    ntEnsurePlaybackSession();
    if (isFreshStart) {
      ntPlaybackCurrentMs = ntPlaybackPoints[0].t;
      // Recenter on the starting point at a close zoom -- only for a real
      // Play press (not resume-from-pause, and not scrubbing), so if the
      // user had panned/zoomed off somewhere else the flight doesn't start
      // off-screen. Zoom 15 reads as roughly a 300m scale bar, matching the
      // "Locate Now" zoom used elsewhere.
      const start = ntPlaybackPoints[0];
      ntMap.setView([start.lat, start.lng], 15, { animate: true });
      ntRenderPlaybackFrame();
    }
    ntResumePlaybackLoop();
  }

  function ntResumePlaybackLoop() {
    if (ntPlaybackTimer || !ntPlaybackMarker) return;
    document.getElementById('nt-play-btn').textContent = '⏸ Pause';
    document.getElementById('nt-stop-btn').disabled = false;
    const endMs = ntPlaybackPoints[ntPlaybackPoints.length - 1].t;
    let lastFrameTime = performance.now();
    const loop = (now) => {
      const speedSecPerAnimSec = parseInt(document.getElementById('nt-play-speed').value, 10);
      // ntPlaybackScanIndex tracks the segment the LAST rendered frame was
      // in -- one frame behind "current" (updated inside
      // ntRenderPlaybackFrame below), which at 60fps is an imperceptible
      // lag and avoids a second position lookup just to check stillness.
      const inStillSegment = ntSkipStillTime && ntStillSegments[ntPlaybackScanIndex];
      const deltaMs = (now - lastFrameTime) * speedSecPerAnimSec * (inStillSegment ? STILL_SPEEDUP_FACTOR : 1);
      lastFrameTime = now;
      ntPlaybackCurrentMs = Math.min(endMs, ntPlaybackCurrentMs + deltaMs);
      ntRenderPlaybackFrame();
      if (ntPlaybackCurrentMs >= endMs) { ntStopPlayback(); return; }
      ntPlaybackTimer = requestAnimationFrame(loop);
    };
    ntPlaybackTimer = requestAnimationFrame(loop);
  }

  function ntPausePlayback() {
    if (ntPlaybackTimer) { cancelAnimationFrame(ntPlaybackTimer); ntPlaybackTimer = null; }
    document.getElementById('nt-play-btn').textContent = '▶ Play';
  }

  // skipRestore=true is used when a fresh render is about to wipe the map
  // anyway (accuracy slider moved, tracker switched, etc.) -- no point
  // restoring the static layers just to have them removed again next line.
  function ntStopPlayback(skipRestore) {
    if (ntPlaybackTimer) { cancelAnimationFrame(ntPlaybackTimer); ntPlaybackTimer = null; }
    if (ntPlaybackMarker) { ntMap.removeLayer(ntPlaybackMarker); ntPlaybackMarker = null; }
    if (ntPlaybackTrail) { ntMap.removeLayer(ntPlaybackTrail); ntPlaybackTrail = null; }
    if (ntPlaybackTrailCasing) { ntMap.removeLayer(ntPlaybackTrailCasing); ntPlaybackTrailCasing = null; }
    if (!skipRestore) ntStaticLayers.forEach(l => l.addTo(ntMap));
    document.getElementById('nt-play-btn').textContent = '▶ Play';
    document.getElementById('nt-play-btn').disabled = ntPlaybackPoints.length < 2;
    document.getElementById('nt-stop-btn').disabled = true;
    const seek = document.getElementById('nt-play-seek');
    seek.disabled = ntPlaybackPoints.length < 2;
    seek.value = 0;
    const timeEl2 = document.getElementById('nt-play-time');
    timeEl2.textContent = 'Press play to replay this track from the first ping to the last.';
    timeEl2.classList.remove('nt-play-time--active');
  }

  // User dragged the seek bar -- pause any active playback and jump the
  // marker/trail/time straight to that point in the recording.
  function ntSeekPlayback(seekValue) {
    if (ntPlaybackPoints.length < 2) return;
    ntPausePlayback();
    ntEnsurePlaybackSession();
    const startMs = ntPlaybackPoints[0].t, endMs = ntPlaybackPoints[ntPlaybackPoints.length - 1].t;
    const frac = seekValue / 1000;
    ntPlaybackCurrentMs = startMs + frac * (endMs - startMs);
    ntRenderPlaybackFrame();

    // Recenter on wherever was scrubbed to if it's not currently visible --
    // matters most on the very first scrub before Play was ever pressed,
    // since the map could still be sitting wherever the user last left it.
    const pos = ntPositionAtTime(ntPlaybackPoints, ntPlaybackCurrentMs);
    if (pos && !ntMap.getBounds().contains([pos.lat, pos.lng])) {
      ntMap.setView([pos.lat, pos.lng], ntMap.getZoom(), { animate: true });
    }
  }

  function ntSetMapType(type) {
    if (!NT_TILE_LAYERS[type] || !ntMap) return;
    ntMapType = type;
    if (ntBaseLayer) ntMap.removeLayer(ntBaseLayer);
    ntBaseLayer = NT_TILE_LAYERS[type]();
    ntBaseLayer.addTo(ntMap);
    // "light" is an L.layerGroup (base + reference tile layers combined, see
    // NT_TILE_LAYERS) -- bringToBack() is a per-tile-layer method, groups
    // don't have it, so each child layer needs it called individually, and
    // in REVERSE order: bringToBack pushes a layer behind whatever's
    // currently at the back, so sending the reference (labels) layer back
    // FIRST and the base (terrain) layer back SECOND is what leaves base at
    // the very bottom with reference sitting visibly on top of it -- doing
    // it in forward order would bury the labels underneath the terrain.
    // Plain single-layer styles (detailed/satellite) still just call it
    // directly on themselves.
    if (ntBaseLayer.getLayers) {
      ntBaseLayer.getLayers().reverse().forEach(l => l.bringToBack());
    } else {
      ntBaseLayer.bringToBack();
    }
    // Keeps the collapsed attribution control (NtAttributionControl) showing
    // the RIGHT credit for whichever style is now active -- Esri for
    // light/satellite, OpenStreetMap for detailed.
    if (ntAttributionTextEl) ntAttributionTextEl.innerHTML = 'FlightPathLive | ' + NT_TILE_ATTRIBUTION[type];
  }

  // NtMapTypeControl/NtFullscreenControl are defined INSIDE
  // initNewTrackMap() (not at top level) because they call
  // L.Control.extend(...) immediately -- if Leaflet hasn't been
  // lazy-loaded yet, L would be undefined and that call would throw,
  // aborting the rest of this script. Defining them only after
  // ensureLeafletLoaded() resolves keeps them safe either way.
  let ntMapReadyPromise = null;
  function initNewTrackMap() {
    if (ntMapReadyPromise) return ntMapReadyPromise;
    ntMapReadyPromise = ensureLeafletLoaded().then(() => {
      // Collapsed to a single layers-stack icon button by default -- clicking
      // it opens the Light/Detailed/Satellite picker above the button, and
      // picking an option closes it again.
      const NtMapTypeControl = L.Control.extend({
        options: { position: 'topright' },
        onAdd: function () {
          const wrap = L.DomUtil.create('div', 'nt-maptype-wrap nt-maptype-wrap--top');
          wrap.innerHTML = `
            <div class="nt-maptype-panel">
              <label><input type="radio" name="nt-maptype" value="light"> Light</label>
              <label><input type="radio" name="nt-maptype" value="detailed" checked> Detailed</label>
              <label><input type="radio" name="nt-maptype" value="satellite"> Satellite</label>
              <hr style="margin:6px 0;border:none;border-top:1px solid #e5e8eb;">
              <label><input type="checkbox" id="nt-layer-distance-rings"> Distance from loft</label>
              <label><input type="checkbox" id="nt-layer-loft-names"> Display loft names</label>
              <label><input type="checkbox" id="nt-layer-tracker-names"> Display tracker names</label>
            </div>
            <button type="button" class="nt-maptype-toggle" aria-label="Map type"><i class="fa-solid fa-layer-group"></i></button>
          `;
          L.DomEvent.disableClickPropagation(wrap);
          wrap.querySelector('.nt-maptype-toggle').addEventListener('click', () => {
            wrap.classList.toggle('open');
          });
          wrap.querySelectorAll('input[name="nt-maptype"]').forEach(input => {
            input.addEventListener('change', () => {
              ntSetMapType(input.value);
              wrap.classList.remove('open');
            });
          });
          wrap.querySelector('#nt-layer-distance-rings').addEventListener('change', (e) => {
            ntShowDistanceRings = e.target.checked;
            ntRenderExtraLayers();
          });
          wrap.querySelector('#nt-layer-loft-names').addEventListener('change', (e) => {
            ntShowLoftNames = e.target.checked;
            ntRenderExtraLayers();
          });
          // Affects the tracker's own current-position marker (built inside
          // renderNewTrackMap's point loop, not the extra-layers group), so
          // this needs the full local re-render, same cheap re-run the
          // accuracy slider and spike toggle already use -- no re-fetch.
          wrap.querySelector('#nt-layer-tracker-names').addEventListener('change', (e) => {
            ntShowTrackerNames = e.target.checked;
            renderNewTrackContent();
          });
          return wrap;
        }
      });

      // Fullscreens the whole map column (map + play/seek controls + legend),
      // not just the map tile -- built by hand instead of using a plugin
      // because Leaflet.fullscreen only ever fullscreens the map's own
      // container, and the Play/Stop/seek bar/legend live outside of it as
      // sibling elements.
      const NtFullscreenControl = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: function () {
          const btn = L.DomUtil.create('button', 'nt-fullscreen-btn');
          btn.type = 'button';
          btn.setAttribute('aria-label', 'View fullscreen');
          btn.innerHTML = '<i class="fa-solid fa-expand"></i>';
          L.DomEvent.disableClickPropagation(btn);
          const wrap = document.querySelector('.nt-map-col');
          btn.addEventListener('click', () => {
            if (document.fullscreenElement === wrap) {
              document.exitFullscreen();
            } else if (wrap.requestFullscreen) {
              wrap.requestFullscreen();
            } else if (wrap.webkitRequestFullscreen) {
              wrap.webkitRequestFullscreen();
            }
          });
          document.addEventListener('fullscreenchange', () => {
            const isFull = document.fullscreenElement === wrap;
            btn.innerHTML = isFull ? '<i class="fa-solid fa-compress"></i>' : '<i class="fa-solid fa-expand"></i>';
            setTimeout(() => ntMap.invalidateSize(), 50);
          });
          return btn;
        }
      });

      // Collapsed by default to a small "i" icon instead of always showing
      // the full attribution text inline -- OpenStreetMap/Esri's required
      // credits stay fully intact and reachable (tap to expand), just less
      // visually loud, since Leaflet's default attribution control has no
      // built-in way to start collapsed. Replaces the default control
      // entirely (attributionControl:false on map creation below).
      const NtAttributionControl = L.Control.extend({
        options: { position: 'bottomright' },
        onAdd: function () {
          const wrap = L.DomUtil.create('div', 'nt-attribution-wrap');
          wrap.innerHTML = `
            <div class="nt-attribution-text"></div>
            <button type="button" class="nt-attribution-toggle" aria-label="Map credits">i</button>
          `;
          L.DomEvent.disableClickPropagation(wrap);
          ntAttributionTextEl = wrap.querySelector('.nt-attribution-text');
          wrap.querySelector('.nt-attribution-toggle').addEventListener('click', () => {
            wrap.classList.toggle('open');
          });
          return wrap;
        }
      });

      // Shows the VIEWER's own current location (browser Geolocation API),
      // not the tracker's -- purely client-side, nothing saved to Firebase.
      // Reuses the same blue-dot pulse marker style as elsewhere, removing/
      // replacing it on each click rather than stacking multiple markers.
      const NtLocateMeControl = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: function () {
          const btn = L.DomUtil.create('button', 'nt-fullscreen-btn');
          btn.type = 'button';
          btn.setAttribute('aria-label', 'Show my location');
          btn.innerHTML = '<i class="fa-solid fa-location-crosshairs"></i>';
          L.DomEvent.disableClickPropagation(btn);
          btn.addEventListener('click', () => {
            if (!navigator.geolocation) {
              showToast('Location isn\'t supported by this browser.');
              return;
            }
            btn.disabled = true;
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                btn.disabled = false;
                const { latitude, longitude } = pos.coords;
                if (ntMyLocationMarker) ntMap.removeLayer(ntMyLocationMarker);
                ntMyLocationMarker = L.marker([latitude, longitude], {
                  icon: L.divIcon({ className: '', html: grpPulseIconHtml('#2f6fed'), iconSize: [44, 44], iconAnchor: [22, 22] })
                }).addTo(ntMap);
                ntMyLocationMarker.bindPopup('<b>You are here</b>').openPopup();
                ntMap.setView([latitude, longitude], 15, { animate: true });
              },
              () => {
                btn.disabled = false;
                showToast('Could not get your location — check location permission for this site.');
              },
              { enableHighAccuracy: true, timeout: 10000 }
            );
          });
          return btn;
        }
      });

      // Reverted the Canvas renderer experiment (was meant to fix a rare
      // playback "blinking" when the marker went off-screen) -- it broke
      // something worse: the static path/point markers (all vector layers --
      // polylines, circleMarkers) stopped rendering at all on this map
      // instance, leaving only the HTML-based pulse marker visible. Back to
      // Leaflet's default SVG renderer, which is reliable here.
      // attributionControl:false -- replaced entirely by NtAttributionControl
      // (collapsed-by-default "i" icon) instead of Leaflet's own always-
      // visible one, since Leaflet has no built-in way to start collapsed.
      ntMap = L.map('nt-map', { attributionControl: false }).setView([51.5074, -0.1278], 6);
      new NtMapTypeControl().addTo(ntMap);
      L.control.scale({ position: 'bottomleft', metric: true, imperial: false, maxWidth: 150 }).addTo(ntMap);
      // Added before Fullscreen so it lands right after the +/- zoom
      // buttons in the topleft stack, ahead of the fullscreen icon below it.
      new NtLocateMeControl().addTo(ntMap);
      new NtFullscreenControl().addTo(ntMap);
      // Added before ntSetMapType() so ntAttributionTextEl already exists
      // the first time it tries to populate the credit text below.
      new NtAttributionControl().addTo(ntMap);
      ntSetMapType(ntMapType);
      ntMapInitialized = true;
    });
    return ntMapReadyPromise;
  }

  function populateNewTrackDropdown() {
    const select = document.getElementById('nt-tracker-select');
    const previousValue = select.value;
    select.innerHTML = '<option value="">Select a tracker...</option>';
    Object.keys(registry).sort().forEach(rawId => {
      const pigeon = matchedPigeon(rawId);
      const label = pigeon ? `${pigeon.name} (${rawId})` : rawId;
      const opt = document.createElement('option');
      opt.value = rawId;
      opt.textContent = label;
      select.appendChild(opt);
    });
    if (previousValue && registry[previousValue]) select.value = previousValue;
  }

  // Sidebar badge counts, kept in sync from the raw listener data directly
  // -- renderTrackersTab()/renderGroupTrackingList() only run their full
  // (DOM-heavy) render while that tab is actually the active one, so the
  // sidebar badge would otherwise stay blank until each tab was visited
  // at least once. These are cheap enough to just run on every update.
  function updateTrackerNavCount() {
    const count = (isAdmin ? Object.keys(registry).length : Object.keys(myTrackerMemberships).length);
    document.getElementById('db-nav-trk-count').textContent = count || '';
    updateTrackerLimitDisplay();
  }
  // Shows "X / Y trackers used" next to "+ Add Tracker" -- blank for admin
  // (exempt from the limit entirely) or before myTrackerLimit has loaded.
  // Piggybacks on updateTrackerNavCount's own call sites so it stays in
  // sync automatically whenever trackerMemberships changes, without a
  // separate listener of its own.
  function updateTrackerLimitDisplay() {
    const el = document.getElementById('trk-limit-display');
    if (!el) return;
    if (isAdmin || myTrackerLimit == null) { el.textContent = ''; return; }
    const used = Object.keys(myTrackerMemberships).length;
    el.textContent = `${used} / ${myTrackerLimit} tracker${myTrackerLimit === 1 ? '' : 's'} used`;
    // Red once they've actually hit (or somehow exceeded) the cap -- the
    // point it's genuinely actionable -- otherwise the normal muted gray.
    // Toggled via class (not inline style) so dark mode can style each
    // state correctly instead of fighting a JS-set inline color.
    el.className = used >= myTrackerLimit ? 'trk-limit-warn' : 'trk-limit-ok';
  }
  function updateGroupNavCount() {
    // Owned + joined together -- same reasoning as Trackers' own count,
    // this is "how many groups does Group Tracking have something to show
    // you," not just how many you created yourself.
    const count = Object.keys(trackerGroups).length + Object.keys(joinedFlights).length;
    document.getElementById('db-nav-grp-count').textContent = count || '';
  }

